import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Zap, CheckCircle, Link as LinkIcon, AlertCircle, RefreshCw, Loader } from "lucide-react";
import PDLList from "./PDLList";

const normStatus = (s) => String(s || "").trim().toLowerCase();

const PDLManager = () => {
  const { userProfile, session } = useOutletContext();

  const [pdls, setPdls] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = session?.access_token;
  const rang = userProfile?.rang;

  const loadData = useCallback(async () => {
    if (!token || rang === undefined || rang === null) return;

    try {
      setLoading(true);

      // ✅ rang 0 ET 8 = admin-like
      const isAdminLike = rang === 0 || rang === 8;
      const endpoint = isAdminLike ? "/api/pdl/admin/all" : "/api/pdl/";

      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();

      if (result?.success) {
        const rows = result.data || [];
        setPdls(rows);

        // debug 2 min si besoin
        // console.log("[PDL] statuses uniques:", [...new Set(rows.map(r => r.connection_status))]);
      } else {
        console.error("[PDL] Erreur API:", result?.message);
        setPdls([]);
      }
    } catch (err) {
      console.error("[PDL] Erreur chargement:", err);
      setPdls([]);
    } finally {
      setLoading(false);
    }
  }, [token, rang]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(() => {
    const total = pdls.length;
    const validated = pdls.filter((p) => !!p.is_validated).length;
    const connected = pdls.filter((p) => normStatus(p.connection_status) === "connected").length;

    // En attente = not_connected + pending (comme tu faisais déjà dans Compact)
    const pending = pdls.filter((p) => {
      const st = normStatus(p.connection_status);
      return st === "not_connected" || st === "pending";
    }).length;

    const errors = pdls.filter((p) => {
      const st = normStatus(p.connection_status);
      return st === "error" || st === "token_expired";
    }).length;

    const synced = pdls.filter((p) => !!p.last_sync_date).length;

    return { total, validated, connected, pending, errors, synced };
  }, [pdls]);

  if (loading && pdls.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader className="h-6 w-6 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3">
      <div className="max-w-7xl mx-auto space-y-3">
        {/* ===== Header ===== */}
        <div className="bg-white rounded shadow border border-gray-200">
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-blue-600" />
                  <h1 className="text-xs font-bold text-gray-900 truncate">
                    Mes Points de Livraison Électricité
                  </h1>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Consultez vos PDL et visualisez leur statut Enedis.
                </p>
              </div>

              <button
                type="button"
                onClick={loadData}
                className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50"
                title="Actualiser"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Actualiser
              </button>
            </div>
          </div>

          {/* ===== KPIs ===== */}
          <div className="p-3">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-600">Total PDL</div>
                    <div className="text-xs font-bold text-gray-900">{stats.total}</div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Zap className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-600">Connectés</div>
                    <div className="text-xs font-bold text-gray-900">{stats.connected}</div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-600">En attente</div>
                    <div className="text-xs font-bold text-gray-900">{stats.pending}</div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center">
                    <LinkIcon className="h-4 w-4 text-yellow-700" />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-600">Erreurs</div>
                    <div className="text-xs font-bold text-gray-900">{stats.errors}</div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== List (filtres + table) ===== */}
        <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
          <PDLList pdls={pdls} onRefresh={loadData} />
        </div>
      </div>
    </div>
  );
};

export default PDLManager;
